import { Editor } from '@tiptap/core'
import { VFC } from 'react'
import { ZIndex } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { copyToClipboard, ClipboardError } from '@opallabs/lambda'
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  Icon,
  ToggleButtonGroup,
  Tooltip,
} from '@opallabs/lapidary'
import { opalObjectByUrl } from '@opallabs/routing'
import { EmbeddedResourceAttributes } from '../../extensions/EmbeddedResource'
import * as styles from './styles'
import { ActionViewProps } from './types'

// Taken from @tiptap/extension-link package
type LinkAttributes = {
  href: string
  target?: string | null
  rel?: string | null
  class?: string | null
}
const isLinkAttrs = (
  attrs: Record<string, unknown>
): attrs is LinkAttributes => {
  return 'href' in attrs
}

const isEmbedAttrs = (
  attrs: Record<string, unknown>
): attrs is EmbeddedResourceAttributes => {
  return 'embedType' in attrs
}

const getLinkType = (
  editor: Editor
): 'url' | EmbeddedResourceAttributes['embedType'] | undefined => {
  if (isLinkAttrs(editor.getAttributes('link'))) {
    return 'url'
  }

  const embedAttrs = editor.getAttributes('embeddedResource')
  if (isEmbedAttrs(embedAttrs)) {
    return embedAttrs.embedType
  }

  return undefined
}

const getAttributes = (
  editor: Editor
): LinkAttributes | EmbeddedResourceAttributes | undefined => {
  const linkAttrs = editor.getAttributes('link')
  if (isLinkAttrs(linkAttrs)) {
    return linkAttrs
  }

  const embeddedResourceAttrs = editor.getAttributes('embeddedResource')
  if (isEmbedAttrs(embeddedResourceAttrs)) {
    return embeddedResourceAttrs
  }
  return
}

const getHref = (editor: Editor) => getAttributes(editor)?.href ?? ''

export const ActionView: VFC<ActionViewProps> = ({
  editor,
  onClose,
  onEdit,
  wrapperStyle,
}) => {
  const theme = useTheme()

  const handleLinkTypeChange = (value: string) => {
    if (value === 'url') onUrl()
    if (value === 'inline') onInline()
    if (value === 'card') onCard()
  }

  const canInline = () => {
    const href = getHref(editor)

    try {
      return opalObjectByUrl(new URL(href).pathname) !== null
    } catch {
      return false
    }
  }

  const canCard = () => {
    const href = getHref(editor)

    try {
      const pathname = new URL(href).pathname
      return opalObjectByUrl(pathname) !== null && pathname.includes('/posts')
    } catch {
      return false
    }
  }

  const onUrl = () => {
    const linkType = getLinkType(editor)
    const href = getHref(editor)
    const object = opalObjectByUrl(new URL(href).pathname)

    if (!object) return

    if (linkType === 'inline' || linkType === 'card') {
      const embedAttrs = getAttributes(editor) as EmbeddedResourceAttributes
      editor.chain().convertEmbeddedResourceToLink(embedAttrs).run()
    }

    onClose()
  }

  const onInline = () => {
    const href = getHref(editor)
    const label = editor.getAttributes('embeddedResource').label
    const object = opalObjectByUrl(new URL(href).pathname)

    if (!object) return

    editor
      .chain()
      .convertLinkToEmbeddedResource({
        href,
        objectId: object.id,
        objectType: object.type,
        embedType: 'inline',
        label: label,
      })
      .run()

    onClose()
  }

  const onCard = () => {
    const href = getHref(editor)
    const label = editor.getAttributes('embeddedResource').label
    const object = opalObjectByUrl(new URL(href).pathname)

    if (!object) return

    editor
      .chain()
      .convertLinkToEmbeddedResource({
        href,
        objectId: object.id,
        objectType: object.type,
        embedType: 'card',
        label: label,
      })
      .run()

    onClose()
  }

  const handleUnlink = () => {
    // Skip moving cursor post-hide, we're removing
    // the link so we don't need that to happen
    onClose({ moveCursor: false })
    const linkType = getLinkType(editor)
    if (linkType === 'inline' || linkType === 'card') {
      const attrs = getAttributes(editor) as EmbeddedResourceAttributes
      editor.chain().unsetEmbeddedResource(attrs).focus().run()
    } else if (linkType === 'url') {
      editor.chain().unsetLink().focus().run()
    }
  }

  const handleOpen = () => {
    const href = getHref(editor)
    window.open(href, '_blank')
    onClose()
  }

  const handleCopy = async () => {
    try {
      const href = getHref(editor)
      await copyToClipboard(href)
    } catch (err) {
      if (err instanceof ClipboardError) {
        console.debug(err.message)
      }
    }

    onClose()
  }

  return (
    <div css={styles.actionView} style={wrapperStyle}>
      <ToggleButtonGroup.Root
        onVariant="secondary"
        offVariant="tertiary"
        onValueChange={handleLinkTypeChange}
        initialValue={getLinkType(editor) ?? 'url'}
      >
        <ToggleButtonGroup.Button
          value="url"
          buttonText="URL"
          name="url"
          size={ButtonSize.Small}
          icon={<Icon glyph="gem-link" />}
          iconPosition={ButtonIconPosition.leading}
        />
        <ToggleButtonGroup.Button
          value="inline"
          buttonText="Inline"
          name="inline"
          disabled={!canInline()}
          size={ButtonSize.Small}
          icon={<Icon glyph="gem-popout" />}
          iconPosition={ButtonIconPosition.leading}
        />
        <ToggleButtonGroup.Button
          value="card"
          buttonText="Card"
          name="card"
          disabled={!canCard()}
          size={ButtonSize.Small}
          icon={<Icon glyph="gem-card" />}
          iconPosition={ButtonIconPosition.leading}
        />
      </ToggleButtonGroup.Root>

      <div css={styles.actionViewButtonsWrapper({ theme })}>
        <Tooltip content="Edit" sideOffset={4} zIndex={ZIndex.Drawer.Tooltip}>
          <Button
            hideButtonText
            icon={<Icon glyph="gem-edit" />}
            name="Edit Link"
            onClick={onEdit}
            size={ButtonSize.Small}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip content="Open" sideOffset={4} zIndex={ZIndex.Drawer.Tooltip}>
          <Button
            hideButtonText
            icon={<Icon glyph="gem-popout" />}
            name="Open Link"
            onClick={handleOpen}
            size={ButtonSize.Small}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip content="Copy" sideOffset={4} zIndex={ZIndex.Drawer.Tooltip}>
          <Button
            hideButtonText
            icon={<Icon glyph="gem-copy" />}
            name="Copy Link"
            onClick={handleCopy}
            size={ButtonSize.Small}
            variant="tertiary"
          />
        </Tooltip>
        <Tooltip
          content="Remove link"
          sideOffset={4}
          zIndex={ZIndex.Drawer.Tooltip}
        >
          <Button
            hideButtonText
            icon={<Icon glyph="gem-trash" />}
            variant="primaryDestructive"
            size={ButtonSize.Small}
            name="Remove Link"
            onClick={handleUnlink}
          />
        </Tooltip>
      </div>
    </div>
  )
}
